// @ts-nocheck
import DocumentObject from "classes/Documents/DocumentObject";
import documents_treat from "includes/documents_treat";
import formatDate from "includes/formatDate";
import noAccent from "includes/no_accents";
import genRequest from "includes/request";

class EmployeObject {
	#id = null;
	#firstName = "";
	#lastName = "";
	#sexe = null;
	#hiredDate = null;
	#workplace = null;
	#login = null;
	#phone = null;
	#mail = null;
	#siteId = null;
	#aspNetUserId = null;
	#companyId = null;
	#companyRankId = null;
	#workFamillyId = null;
	#workFunctionId = null;
	#lastChanged = null;
	#firstConnexion = null;
	#lastConnexion = null;
	#hobby = null;
	#lastChangeAuthorId = null;
	#tokenRegeneration = null;
	#displayPhone = true;
	#displayMail = true;
	#bypassModeration = null;
	#role = "UserPublisher";
	#oldRole = null;
	#userTemplateId = null;
	#languageId = null;
	#isActive = null;

	#companyName = null;
	#linkedGroups = [];
	#linkToGroups = [];
	#documents = [];
	#password = null;
	#mailStatus = null;
	#isNew = false;
	#rights = [];
	#currentImages = [];
	#image = null;

	constructor(obj) {
		this.#initValues(obj);
	}

	#initValues(obj) {
		let date = formatDate();

		this.#id = obj?.id ? obj.id : null;
		this.#firstName = obj?.firstName ? obj.firstName : "";
		this.#lastName = obj?.lastName ? obj.lastName : "";
		this.#sexe = obj?.sex ?? obj?.sexe ?? "";
		this.#hiredDate = obj?.hiredDate ? obj.hiredDate : null;
		this.#workplace = obj?.workplace ? obj.workplace : "";
		this.#login = obj?.login ? obj.login : "";
		this.#phone = obj?.telephone ?? obj?.phone ?? "";
		this.#mail = obj?.mail ? obj.mail : "";
		this.#siteId = obj?.siteId ? obj.siteId : null;
		this.#aspNetUserId = obj?.aspNetUserId ? obj.aspNetUserId : null;
		this.#companyId = obj?.companyId ? obj.companyId : null;
		this.#companyRankId = obj?.companyRankId ? obj.companyRankId : 1;
		this.#workFamillyId = obj?.workFamilyId ?? null;
		this.#workFunctionId = obj?.workFunctionId ? obj.workFunctionId : null;
		this.#lastChanged = obj?.lastChanged ? obj.lastChanged : date;
		this.#firstConnexion = obj?.firstConnexion ? obj.firstConnexion : null;
		this.#lastConnexion = obj?.lastConnexion ? obj.lastConnexion : null;
		this.#hobby = obj?.hobby ? obj.hobby : "";
		this.#lastChangeAuthorId = obj?.lastChangeAuthorId
			? obj.lastChangeAuthorId
			: null;
		this.#tokenRegeneration = obj?.tokenRegeneration
			? obj.tokenRegeneration
			: false;
		this.#displayPhone = obj?.displayPhone ?? true;
		this.#displayMail = obj?.displayMail ?? true;
		this.#bypassModeration = obj?.bypassModeration
			? obj.bypassModeration
			: false;
		this.#role = obj?.role ? obj.role : "UserPublisher";
		this.#userTemplateId = obj?.userTemplateId ? obj.userTemplateId : null;
		this.#languageId = obj?.languageId ? obj.languageId : 1;
		// false must survive (a deactivated employee stays deactivated when edited)
		this.#isActive = obj?.isActive ?? true;

		this.#oldRole = obj?.oldRole ? obj.oldRole : this.#role;

		if (!this.#id) this.#isNew = true;
	}

	fetchForEdit() {
		let proms = [
			this.fetchCompany(),
			this.fetchRights(),
			this.fetchLinkedGroups(),
			this.fetchLinkedDocs(),
			this.fetchProfileImage(),
		];
		return Promise.all(proms);
	}

	fetchCompany() {
		let that = this;
		let req = genRequest("companies/" + this.#companyId).then((resp) => {
			that.#companyName = resp.name;
		});
		return req;
	}

	send() {
		if (this.#role !== this.#oldRole && this.#id)
			return this.sendWithRole();
		return this.sendUser();
	}

	#toCamelCase() {
		return {
			firstName: this.#firstName,
			lastName: this.#lastName,
			mail: this.#mail,
			phone: this.#phone,
			sexe: this.#sexe,
			hobby: this.#hobby,
			role: this.#role,
			siteId: this.#siteId,
			companyRankId: this.#companyRankId,
			workFamilyId: this.#workFamillyId,
			workFunctionId: this.#workFunctionId,
			workplace: this.#workplace,
			displayPhone: this.#displayPhone,
			displayMail: this.#displayMail,
			bypassModeration: this.#bypassModeration,
			isActive: this.#isActive,
		};
	}

	// New employee: login account + employee + default rights of the role are
	// created in one request (the password is mailed with the login).
	sendUser() {
		let that = this;
		let camelData = this.#toCamelCase();

		if (!this.#id) {
			camelData.companyId = this.#companyId;
			camelData.login = this.#login || this.#mail;
			camelData.password = this.#password;
			if (this.#hiredDate) camelData.hiredDate = this.#hiredDate;
		}

		let req = genRequest(
			"employees" + (this.#id ? "/" + this.#id : ""),
			camelData,
			this.#id ? "put" : "post"
		).then((resp) => {
			if (!that.#id) {
				that.#mailStatus = resp.emailStatus;
				that.#initValues(resp);
			}
			return resp;
		});
		return req;
	}

	// The role change is part of the same PUT: the API replaces the login role.
	sendWithRole() {
		if (!this.#id)
			return Promise.reject(new Error("No Employe Id"));
		return this.sendUser();
	}

	// Login and initial password are chosen here; the account itself is created
	// by sendUser (nothing is sent to the API at this step).
	register(login, password, mail) {
		let name = noAccent(
			(this.#firstName[0] + "." + this.#lastName)
				.trim()
				.slice(0, 10)
				.replace(/\s/gi, "_")
		);
		this.#password = password?.length ? password : randomPsw(8);
		if (mail?.length) this.#mail = mail;
		this.#login = (login?.length ? login : this.#mail || name + "_" + this.#companyName)
			.toLowerCase()
			.replace(/\s/gi, "_");
		return Promise.resolve({ username: this.#login });
	}

	// The credentials mail is sent by the API when the employee is created: this
	// step reports a delivery failure (a missing SMTP setup is not a failure).
	sendConfirmMail() {
		if (this.#mailStatus === "failed")
			return Promise.reject(new Error("MAIL_NOT_SENT"));
		return Promise.resolve(this.#mailStatus);
	}

	fetchProfileImage() {
		let that = this;

		if (!this.#id) return Promise.resolve(true);

		let req = genRequest(
			"documents/employee/" + this.#id + "/type/2"
		).then((images) => {
			that.#currentImages = images?.length ? images : [];
			return images;
		});
		return req;
	}

	image(setImage) {
		if (typeof setImage !== "undefined") this.#image = setImage;
		return this.#image;
	}

	#clearOldImages() {
		let proms = [];
		if (this.#currentImages?.length) {
			proms = this.#currentImages.map((a) => {
				let doc = new DocumentObject(a);
				return doc.delete(false, true);
			});
		}
		return Promise.allSettled(proms);
	}

	sendImage() {
		if (!this.#image) return Promise.resolve(true);
		this.#image.resetLinks(
			this.#companyId,
			this.#siteId,
			this.#id,
			false
		);
		let proms = [this.#clearOldImages(), this.#image.send()];
		return Promise.allSettled(proms);
	}

	fetchRights() {
		let that = this;
		if (!this.#id) return [];
		let req = genRequest(
			"permissions/employee/" + this.#id
		).then(
			(resp) => (that.#rights = resp),
			() => (that.#rights = [])
		);
		return req;
	}

	// Rights are sent by name: the ids of the rights table are the API's own.
	sendRights() {
		if (!this.#id)
			return Promise.reject(
				new Error("No EmployeeId, send employe before rights link")
			);
		return genRequest("permissions/rights").then((all) => {
			let byName = new Map(all.map((r) => [r.name, r.id]));
			let rights = this.#rights
				.map((a) => byName.get(a.name ?? a.rightName))
				.filter((id) => id)
				.map((id) => ({ userRightId: id }));
			return this.deleteRights().then(() =>
				genRequest(
					"permissions/employee/" + this.#id + "/rights/batch",
					{ rights },
					"post"
				)
			);
		});
	}

	deleteRights = () =>
		genRequest(
			"permissions/employee/" + this.#id + "/rights",
			null,
			"delete"
		);

	rights(only_perms) {
		if (!only_perms) return this.#rights;
		let ret = {};
		if (!this.#rights.length) return false;
		this.#rights.forEach((a) => {
			ret[a.rightName] = true;
		});
		return ret;
	}

	setRights(values) {
		if (typeof values !== "undefined") this.#rights = values;
		return this.#rights;
	}

	// Groups

	fetchLinkedGroups() {
		if (!this.#id) return [];
		let that = this;
		let req = genRequest(
			"groups/employee/" + this.#id,
			null,
			"get"
		).then(
			(resp) => {
				that.#linkedGroups = resp?.length ? resp : [];
				return that.#linkedGroups;
			},
			() => {
				that.#linkedGroups = [];
				return [];
			}
		);
		return req;
	}

	sendGroups() {
		let that = this;
		let prom = new Promise((resolve, reject) => {
			that.unlinkGroups()
				.then(() => that.linkToGroups())
				.then(resolve, reject);
		});
		return prom;
	}

	linkToGroups() {
		if (!this.#linkToGroups?.length) return true;
		let proms = this.#linkToGroups.map((a) => {
			return this.linkToGroup(a.id || a.groupId);
		});
		return Promise.all(proms);
	}

	linkToGroup = (groupId) =>
		genRequest(
			"groups/" + groupId + "/members/" + this.#id,
			null,
			"post"
		);

	unlinkGroups(groupId) {
		let that = this;
		if (groupId) {
			let req = genRequest(
				"groups/" + groupId + "/members/" + this.#id,
				null,
				"delete"
			).then(() => {
				for (let x of that.#linkedGroups)
					if ((x.id || x.groupId) === groupId) {
						that.#linkedGroups.splice(that.#linkedGroups.indexOf(x), 1);
						break;
					}
			});
			return req;
		}
		// Unlink from all groups: fetch employee groups then remove each
		return genRequest("groups/employee/" + this.#id).then((groups) => {
			if (!groups?.length) {
				that.#linkedGroups = [];
				return;
			}
			let proms = groups.map((g) =>
				genRequest(
					"groups/" + g.id + "/members/" + that.#id,
					null,
					"delete"
				)
			);
			return Promise.all(proms).then(() => {
				that.#linkedGroups = [];
			});
		});
	}

	linkedGroups = () => this.#linkedGroups;

	groupsToLink(setGroups) {
		if (typeof setGroups !== "undefined") this.#linkToGroups = setGroups;
		return this.#linkToGroups;
	}

	/*
	 ** -- Documents
	 */

	fetchDocs(typeId) {
		let url = "documents/employee/" + this.#id;
		if (typeId)
			url =
				"documents/employee/" +
				this.#id +
				"/type/" +
				typeId;
		let req = genRequest(url).then((resp) => {
			return resp;
		});
		return req;
	}

	fetchLinkedDocs() {
		if (!this.#id) return Promise.resolve(true);
		let req = genRequest(
			"documents/employee/" + this.#id + "/type/1"
		).then((resp) => {
			let treated = documents_treat(resp);
			this.#documents = treated;
			return treated;
		});
		return req;
	}

	sendDocs() {
		if (!this.#documents?.length) return Promise.resolve(true);
		let links = this.#documents.map((a) => {
			let temp = a;
			a.employeeId(this.#id);
			if (a.isNew()) {
				temp.documentId(null);
				temp.urls(null);
				return temp.send();
			}
			return temp.linkDoc();
		});
		return Promise.all(links);
	}

	unlinkDocs() {
		if (!this.#id) return Promise.resolve(true);
		let prom = genRequest(
			"documents/employee/" + this.#id,
			null,
			"delete"
		)
			.catch(() => Promise.resolve(true))
			.then(() => Promise.resolve(true));
		return prom;
	}

	documents(setDocs) {
		if (typeof setDocs !== "undefined") this.#documents = setDocs;
		return this.#documents;
	}

	/*
	 ** -- GET / SET
	 */

	isNew = () => this.#isNew;

	employeeId = () => this.#id;

	firstName(setFirstName) {
		if (typeof setFirstName !== "undefined") this.#firstName = setFirstName;
		return this.#firstName;
	}

	lastName(setLastName) {
		if (typeof setLastName !== "undefined") this.#lastName = setLastName;
		return this.#lastName;
	}

	sex(setSex) {
		if (typeof setSex !== "undefined") this.#sexe = setSex;
		return this.#sexe;
	}

	hiredDate(setHiredDate) {
		if (typeof setHiredDate !== "undefined") this.#hiredDate = setHiredDate;
		return this.#hiredDate;
	}

	workplace(setWorkplace) {
		if (typeof setWorkplace !== "undefined") this.#workplace = setWorkplace;
		return this.#workplace;
	}

	login = (setLogin) => {
		if (typeof setLogin !== "undefined") this.#login = setLogin;
		return this.#login;
	};

	telephone(setTelephone) {
		if (typeof setTelephone !== "undefined") this.#phone = setTelephone;
		return this.#phone;
	}

	mail(setMail) {
		if (typeof setMail !== "undefined") this.#mail = setMail;
		return this.#mail;
	}

	siteId(setSiteId) {
		if (typeof setSiteId !== "undefined") this.#siteId = setSiteId;
		return this.#siteId;
	}

	aspNetUserId() {
		return this.#aspNetUserId;
	}

	companyId(setCompanyId) {
		if (typeof setCompanyId !== "undefined") this.#companyId = setCompanyId;
		return this.#companyId;
	}

	companyRankId(setCompanyRankId) {
		if (typeof setCompanyRankId !== "undefined")
			this.#companyRankId = setCompanyRankId;
		return this.#companyRankId;
	}

	workFamilyId(setWorkFamilyId) {
		if (typeof setWorkFamilyId !== "undefined")
			this.#workFamillyId = setWorkFamilyId;
		return this.#workFamillyId;
	}

	workFunctionId(setWorkFunctionId) {
		if (typeof setWorkFunctionId !== "undefined")
			this.#workFunctionId = setWorkFunctionId;
		return this.#workFunctionId;
	}

	lastChanged(setLastChanged) {
		if (typeof setLastChanged !== "undefined")
			this.#lastChanged = setLastChanged;
		return this.#lastChanged;
	}

	firstConnexion(setFirstConnexion) {
		if (typeof setFirstConnexion !== "undefined")
			this.#firstConnexion = setFirstConnexion;
		return this.#firstConnexion;
	}

	lastConnexion(setLastConnexion) {
		if (typeof setLastConnexion !== "undefined")
			this.#lastConnexion = setLastConnexion;
		return this.#lastConnexion;
	}

	hobby(setHobby) {
		if (typeof setHobby !== "undefined") this.#hobby = setHobby;
		return this.#hobby;
	}

	lastChangeAuthorId(setLastChangeAuthorId) {
		if (typeof setLastChangeAuthorId !== "undefined")
			this.#lastChangeAuthorId = setLastChangeAuthorId;
		return this.#lastChangeAuthorId;
	}

	tokenRegeneration(setTokenRegeneration) {
		if (typeof setTokenRegeneration !== "undefined")
			this.#tokenRegeneration = setTokenRegeneration;
		return this.#tokenRegeneration;
	}

	displayPhone(setDisplayPhone) {
		if (typeof setDisplayPhone !== "undefined")
			this.#displayPhone = setDisplayPhone;
		return this.#displayPhone;
	}

	displayMail(setDisplayMail) {
		if (typeof setDisplayMail !== "undefined")
			this.#displayMail = setDisplayMail;
		return this.#displayMail;
	}

	bypassModeration(setByPass_Moderation) {
		if (typeof setByPass_Moderation !== "undefined")
			this.#bypassModeration = setByPass_Moderation;
		return this.#bypassModeration;
	}

	role(setRole) {
		if (typeof setRole !== "undefined") {
			if (typeof setRole === "string") this.#role = setRole;
			else {
				this.bypassModeration(setRole.bypassModeration);
				this.#role = setRole.value;
			}
		}
		return this.#role;
	}

	userTemplateId(setUserTemplateId) {
		if (typeof setUserTemplateId !== "undefined")
			this.#userTemplateId = setUserTemplateId;
		return this.#userTemplateId;
	}

	languageId(setLanguageId) {
		if (typeof setLanguageId !== "undefined")
			this.#languageId = setLanguageId;
		return this.#languageId;
	}

	isVisibleInApp(setIsVisibleInApp) {
		if (typeof setIsVisibleInApp !== "undefined")
			this.#isActive = setIsVisibleInApp;
		return this.#isActive;
	}

	values() {
		let vals = {
			id: this.#id,
			firstName: this.#firstName,
			lastName: this.#lastName,
			sexe: this.#sexe,
			hiredDate: this.#hiredDate,
			workplace: this.#workplace,
			login: this.#login,
			phone: this.#phone,
			mail: this.#mail,
			siteId: this.#siteId,
			aspNetUserId: this.#aspNetUserId,
			companyId: this.#companyId,
			companyRankId: this.#companyRankId,
			workFamilyId: this.#workFamillyId,
			workFunctionId: this.#workFunctionId,
			lastChanged: this.#lastChanged,
			firstConnexion: this.#firstConnexion,
			lastConnexion: this.#lastConnexion,
			hobby: this.#hobby,
			lastChangeAuthorId: this.#lastChangeAuthorId,
			tokenRegeneration: this.#tokenRegeneration,
			displayPhone: this.#displayPhone,
			displayMail: this.#displayMail,
			bypassModeration: this.#bypassModeration,
			role: this.#role,
			userTemplateId: this.#userTemplateId,
			languageId: this.#languageId,
			isActive: this.#isActive,
		};

		// the employee id is aliased once here: `id` for the API, `employeeId` for the screens
		if (!vals.id) delete vals.id;
		else vals.employeeId = vals.id;
		return vals;
	}
}

function randomPsw(length) {
	let sample = [
		"123456789",
		"ABCDEFGHJKMNPQRSTUVWXYZ",
		"abcdefghjkmnopqrstuvwxyz",
		"!._-",
	];
	let check = [0, 0, 0, 0];
	let wrd = "";
	const reducer = function (accumulator, currentValue) {
		return accumulator + currentValue;
	};
	while (check.reduce(reducer) < sample.length) {
		check = [0, 0, 0, 0];
		wrd = "";
		for (let x = 0; x < length; x++) {
			let randCat = parseInt(Math.random() * sample.length);
			if (randCat !== 3 || (check[3] !== 1 && x > 1)) {
				check[randCat] = 1;
				wrd =
					wrd +
					sample[randCat][
						parseInt(Math.random() * sample[randCat].length)
					];
			} else x--;
		}
	}
	return wrd;
}

export default EmployeObject;
